// @flow

import * as React from "react";

import wrapEl from "../../utils/wrapEl";
import scrollToEl from "../../utils/scrollToEl";

type PropsType = {
    html?: string,
    background?: boolean,
    className?: string,
    children?: React.Node,
    theme?: string,
    isContentPageComponent?: boolean,
};

const RichTextContent = (props: PropsType): React.Node => {
    const htmlRef = React.useRef(null);
    const classList = ["rich-text-content"];
    const [expandMobile, setExpandMobile] = React.useState(
        props.isContentPageComponent ? true : false
    );
    const [enableCollapsible, setEnableCollapsible] = React.useState(false);

    if (props.background) {
        classList.push("rich-text-content--colored");
    }
    if (props.className) {
        classList.push(props.className);
    }
    if (props.theme) {
        classList.push(`rich-text-content--${props.theme}`);
    }

    // Modify contents of HTML as necessary
    React.useEffect(() => {
        if (htmlRef.current) {
            const ref = htmlRef.current;

            // Add inner elements to all buttons
            const buttons = Array.from(
                ref.querySelectorAll(
                    ".rte-primary-button, .rte-secondary-button"
                )
            );
            buttons.forEach((button: HTMLElement) => {
                button.innerHTML = `<div class="button__inner">${button.textContent}</div>`;
            });

            // Add wrapper around all tables
            const tables = ref.querySelectorAll("table");
            for (const table of tables) {
                const outer = document.createElement("div");
                const inner = document.createElement("div");
                outer.classList.add("table-wrapper");
                inner.classList.add("table-wrapper__inner");
                wrapEl(table, outer);
                wrapEl(table, inner);
            }

            // Add collapsible on mobile for height greater than 300
            if (!props.background && ref.clientHeight > 400) {
                setEnableCollapsible(true);
            }

            if (props.isContentPageComponent) {
                setEnableCollapsible(false);
            }

            // Add anchor scrolling behaviour
            ref.addEventListener("click", (event: MouseEvent) => {
                const { target } = event;

                if (
                    target instanceof HTMLAnchorElement &&
                    (target.getAttribute("href") || "").substr(0, 1) === "#"
                ) {
                    const id = "#" + target.href.split("#")[1];
                    const el = document.querySelector(id);
                    if (el) {
                        scrollToEl(el);
                        event.preventDefault();
                    }
                }
            });
        }
    }, [props.html]); // eslint-disable-line

    let rawHtml = props.html || "";
    rawHtml = rawHtml
        .replace(/height: \d+(\.\d+)?[%px]+;/gi, "")
        .replace(/width: \d+(\.\d+)?[%px]+;/gi, "");

    if (enableCollapsible) {
        classList.push("rich-text-content--collapsible");
    }

    return props.html ? (
        <React.Fragment>
            <div
                className={
                    classList.join(" ") + `${expandMobile ? " expanded" : ""}`
                }
            >
                <div
                    ref={htmlRef}
                    className="rich-text-content__inner"
                    dangerouslySetInnerHTML={{ __html: rawHtml }}
                ></div>
            </div>
            {enableCollapsible ? (
                <button
                    className="button button--green-secondary rich-text-content__collapse"
                    onClick={() => setExpandMobile(!expandMobile)}
                >
                    {expandMobile ? "Collapse" : "Show more"}
                </button>
            ) : null}
        </React.Fragment>
    ) : (
        <div ref={htmlRef} className={classList.join(" ")}>
            {props.children}
        </div>
    );
};

RichTextContent.defaultProps = {
    backgroundColor: "white",
};

export default RichTextContent;
